import React, { useState, useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '@/hooks/useLanguage';
import { useSubscription } from '@/hooks/useSubscription';
import { hasLiveStripeSubscription, isCapTier } from '@/lib/plans';
import { Trash2, Mail, Globe, Cookie } from 'lucide-react';
import {
  parseConsentCookie,
  effectiveConsent,
  resolveConsentRegime,
  writeConsentCookie,
  applyConsentToGoogle,
  privacyPolicyUrl,
  type ConsentState,
} from '@/lib/consent';
import { pushUserDataIfConsented, clearUserDataForConversions } from '@/lib/analytics';
import { readOwnerMustTransfer } from '@/lib/mcMembership';
import { Checkbox } from '@/components/ui/checkbox';
import { format } from 'date-fns';
import { de as deLocale, enUS } from 'date-fns/locale';

const GeneralTab: React.FC = () => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation('settings');
  const { language, setLanguage } = useLanguage();
  const { subscription } = useSubscription();
  // UX-Audit A6G-02: gesperrt wird nur, was sich kuendigen laesst, ein
  // lebendes Stripe-Abo. Ein Sondertarif ohne Stripe-Abo (Agentur) landete
  // sonst in einer Sackgasse: der Dialog schickte auf "Abonnement verwalten",
  // und das Portal kennt dieses Konto nicht. Ruling R4/R10, fail-closed: ein
  // Katalog-Band im Status active/trialing sperrt auch ohne Stripe-Abo-ID
  // (Webhook-Drift), sonst liefe das Abo bei Stripe verwaist weiter.
  // ⚠ SYNC REQUIRED mit subscriptionGuardResponse in
  // supabase/functions/delete-user-account/owner-guard.ts.
  const hasActiveSubscription =
    !!subscription &&
    subscription.plan_tier !== 'free' &&
    (hasLiveStripeSubscription(subscription) ||
      (isCapTier(subscription.plan_tier) && ['active', 'trialing'].includes(subscription.status)));
  /**
   * M8: the copy for this case says "your plan is already cancelled, wait until
   * {date}", but the only action offered was "Manage subscription" — pointing a
   * user at a cancellation flow they have already been through. There is nothing
   * left to do here, so the dialog offers a plain acknowledgement instead.
   */
  const hasCancelledSubscription = hasActiveSubscription && !!subscription?.cancel_at_period_end;

  /**
   * R2-7: an already-cancelled subscription still blocks account deletion until
   * the paid period ends. Telling that user to "please cancel your subscription"
   * asks for something they have already done, so the cancelled case gets its
   * own copy naming the date the plan actually runs out. A missing or unparsable
   * `current_period_end` falls back to a sentence without a date rather than
   * rendering "until undefined".
   */
  const deleteBlockedDescription = (): string => {
    const plan = subscription?.plan_tier ?? '';
    if (!subscription?.cancel_at_period_end) {
      return t('general.deleteBlockedDesc', { plan });
    }
    const periodEnd = subscription.current_period_end
      ? new Date(subscription.current_period_end)
      : null;
    if (!periodEnd || Number.isNaN(periodEnd.getTime())) {
      return t('general.deleteBlockedDescCancelledNoDate', { plan });
    }
    // The date is rendered in the viewer's LOCAL timezone on purpose. Stripe
    // flips the subscription status at an instant, and `delete-user-account`
    // blocks purely on that status (it selects `status, plan_tier` and never
    // reads `current_period_end`), so the day on which the block lifts *is* the
    // reader's local day. Do not "align this with the server" by shifting to
    // UTC: for a Berlin user whose period ends 2026-09-30T23:00:00Z that would
    // print Sep 30 while the plan actually lapses at 01:00 on Oct 1 local, and
    // they would come back a day early into a 409 — the exact dead end R2-7
    // exists to remove.
    return t('general.deleteBlockedDescCancelled', {
      plan,
      date: format(periodEnd, 'PPP', { locale: language === 'de' ? deLocale : enUS }),
    });
  };
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [removeFromEmailList, setRemoveFromEmailList] = useState(true);
  const [emailLang, setEmailLang] = useState('en');
  const [emailPrefs, setEmailPrefs] = useState({
    onboarding: true,
    achievements: true,
    digests: true,
    upsell: true,
    reengagement: true,
    review: true,
  });
  const [unsubscribedAll, setUnsubscribedAll] = useState(false);
  const [isSavingPrefs, setIsSavingPrefs] = useState(false);

  /**
   * Cookie-Einstellungen: der WIDERRUFSWEG der App.
   *
   * Erteilt wird die Einwilligung auf der Marketing-Seite. Das Cookie gilt fuer
   * `.feedoptimizer.ai` und reist durch OAuth und Magic-Link mit -- die App
   * erbt also `granted` und trackt voll, ohne je selbst gefragt zu haben. Eine
   * erteilte Einwilligung muss so leicht zuruecknehmbar sein, wie sie erteilt
   * wurde; ohne diese Karte gaebe es in der App keinen Weg zurueck.
   *
   * Der Anfangswert kommt aus dem Cookie und wird NICHT vorbelegt: ohne Cookie
   * stehen beide Kategorien auf aus, und das ist der wahre Zustand. Dass er
   * noch nicht ENTSCHIEDEN ist, sagt der Satz in der Karte -- ein dritter
   * Schalterzustand waere eine Erfindung, die das Cookie nicht hergibt.
   *
   * Angezeigt wird der WIRKSAME Zustand (`effectiveConsent`, Spec 3.9), nicht
   * nur der gespeicherte: im US-Regime ohne Cookie laufen Analyse und
   * Marketing per Vorgabe, und Schalter auf "aus" waeren dort die
   * Falschaussage. Mit GPC im US-Regime ist alles aus, auch gegen ein
   * gespeichertes "ja" (E5). Im Opt-in-Regime ohne Cookie bleibt es null.
   */
  const [cookieConsent, setCookieConsent] = useState<ConsentState | null>(() =>
    effectiveConsent(),
  );
  const cookieAnalytics = cookieConsent?.analytics ?? false;
  const cookieMarketing = cookieConsent?.marketing ?? false;
  // Reines Lesen im Render (Region-Cookie und GPC), kein eigener Zustand: das
  // Regime aendert sich nicht durch diese Karte, und jeder Render nach einer
  // Schreibung oder dem Neu-Lesen beim Sichtbarwerden sieht es frisch.
  const consentRegime = resolveConsentRegime();
  const cookieGpcLocked = consentRegime === 'us_gpc';

  useEffect(() => {
    if (!user?.id) return;
    supabase
      .from('email_preferences' as any)
      .select('language, onboarding, achievements, digests, upsell, reengagement, review, unsubscribed_all')
      .eq('user_id', user.id)
      .single()
      .then(({ data }) => {
        if (data) {
          const d = data as any;
          setEmailLang(d.language || 'en');
          setEmailPrefs({
            onboarding: d.onboarding ?? true,
            achievements: d.achievements ?? true,
            digests: d.digests ?? true,
            upsell: d.upsell ?? true,
            reengagement: d.reengagement ?? true,
            review: d.review ?? true,
          });
          setUnsubscribedAll(d.unsubscribed_all ?? false);
        }
      });
  }, [user?.id]);

  const updateEmailPreference = async (field: string, value: any) => {
    if (!user?.id) return;
    setIsSavingPrefs(true);
    try {
      const { error } = await supabase
        .from('email_preferences' as any)
        .update({ [field]: value })
        .eq('user_id', user.id);
      if (error) throw error;

      if (field === 'language') {
        setEmailLang(value);
      } else if (field === 'unsubscribed_all') {
        setUnsubscribedAll(value);
      } else {
        setEmailPrefs(prev => ({ ...prev, [field]: value }));
      }
      toast.success(t('general.prefsUpdated'));
    } catch (err) {
      console.error('Failed to update email preferences:', err);
      Sentry.captureException(err, { tags: { feature: 'settings', action: 'update-email-preferences' } });
      toast.error(t('general.prefsFailed'));
    } finally {
      setIsSavingPrefs(false);
    }
  };

  const handleLanguageChange = async (lang: 'de' | 'en') => {
    await setLanguage(lang);
  };

  /**
   * Speichern heisst hier: schreiben, an Google melden, bestaetigen. Alles drei
   * sofort und ohne Neuladen.
   *
   * Es gibt bewusst KEINEN Speichern-Knopf daneben. Der Schalter ist die
   * Handlung -- dasselbe Muster wie die sechs E-Mail-Schalter eine Karte
   * weiter unten, und derselbe Grund: ein Knopf laesst jemanden glauben, er
   * habe widerrufen, waehrend nichts geschrieben wurde und weiter getrackt
   * wird. Von den beiden moeglichen Fehlern ist das der teurere. Ein
   * versehentlich umgelegter Schalter ist mit demselben Klick wieder zurueck,
   * und die Meldung sagt an, dass etwas geschrieben wurde.
   *
   * `writeConsentCookie` statt eines eigenen `document.cookie`: dort steckt die
   * Domain-Schreibweise samt Gegenprobe auf den WERT (auf localhost und in
   * Previews verwirft der Browser die Domain-Form still, siehe den Kommentar
   * dort). Und `applyConsentToGoogle` sofort danach, damit die Signale nicht
   * erst beim naechsten Seitenaufbau nachziehen -- ein Widerruf, der bis zum
   * Neuladen wirkungslos bleibt, ist keiner.
   *
   * HIER WIRD NICHTS GEZAEHLT, und das ist eine Entscheidung. Der Zaehler
   * `record_consent_event` misst Banner-Impressionen und die Entscheidungen
   * darauf. Eine Aenderung in den Einstellungen ist weder das eine noch das
   * andere; sie als `decision` ohne zugehoeriges `shown` zu schreiben machte
   * `surface='app'` in derselben Spalte zu etwas anderem als `surface='web_*'`,
   * und die Zustimmungsrate liefe ueber zwei verschiedene Grundgesamtheiten.
   * Tabelle und RPC gehoeren dem Web und bleiben unangetastet.
   */
  const saveCookieConsent = (patch: { analytics?: boolean; marketing?: boolean }) => {
    /*
      Die NICHT geaenderte Kategorie kommt FRISCH aus dem Cookie, nicht aus dem
      Zustand von vorhin. Das Cookie gehoert der Domain und nicht diesem Tab:
      wer nebenan auf der Marketing-Seite Analyse widerruft und hier danach
      Marketing umlegt, bekaeme seine Analyse-Einwilligung sonst ungefragt
      zurueckgeschrieben -- eine Einwilligung, die niemand erteilt hat, und das
      ist kein Anzeigefehler mehr.

      `??` und nicht `||`: `patch.analytics === false` ist eine Angabe und darf
      nicht durch den Cookie-Wert ersetzt werden.
    */
    const aktuell = parseConsentCookie();
    const state: ConsentState = {
      necessary: true,
      analytics: patch.analytics ?? aktuell?.analytics ?? false,
      marketing: patch.marketing ?? aktuell?.marketing ?? false,
    };
    const gespeichert = writeConsentCookie(state);
    /*
      Die Signale gehen in JEDEM Fall raus, auch wenn das Cookie nicht landet:
      ein Widerruf muss sofort wirken, und ein nicht gespeichertes "ja" ist
      folgenlos, weil ein Browser, der unser Cookie verwirft, auch das von
      Google verwirft.
    */
    applyConsentToGoogle(state, language);
    /*
      Enhanced Conversions, und genau hier, weil es sonst nirgends passiert: die
      gehashte E-Mail geht beim Anmelden raus (`pushConversionPrerequisites`),
      und zu diesem Zeitpunkt lag die Einwilligung oft noch nicht vor. Wer sie
      auf DIESER Karte erteilt und danach ohne Neuladen ein Merchant Center
      verbindet, meldete `feed_connected` ohne die Angabe, und Google Ads
      koennte die Konversion dem Klick nicht mehr zuordnen.

      Ein Tor braucht der Aufruf nicht mitzubringen: `pushUserDataIfConsented`
      liest den Cookie-Wert selbst, den `writeConsentCookie` eben geschrieben
      hat. Er kann also nie ohne Einwilligung pushen, und wenn die Schreibung
      nicht gelandet ist (der Fall gleich darunter), liest er `false` und
      schweigt. `state.marketing` davor spart nur den Aufruf im Regelfall
      Widerruf.

      Der `else`-Zweig ist das Gegenstueck (M2, Final-Review 2026-09-16):
      widerruft der Nutzer Marketing im selben Seitenaufbau, in dem er es
      erteilt hatte, traegt der dataLayer den zuvor gepushten Hash noch --
      GTM haelt ihn kumulativ, und `clear_user_data` ist Googles Weg,
      eine vorher gesetzte User-Provided-Data zurueckzunehmen.
    */
    if (state.marketing && user?.email) void pushUserDataIfConsented(user.email);
    else if (!state.marketing) clearUserDataForConversions();
    if (!gespeichert) {
      // Kein Erfolgs-Toast ueber weiterlaufendem Tracking. Die Anzeige zieht
      // NICHT nach, sondern auf das, was wirklich wirkt (das Schreiben ist
      // gescheitert, also gilt weiter die Vorgabe) -- der Schalter springt
      // sichtbar zurueck.
      setCookieConsent(effectiveConsent());
      toast.error(t('general.cookieSaveFailed'));
      return;
    }
    setCookieConsent(state);
    toast.success(t('general.cookieSaved'));
  };

  /**
   * Beim Sichtbarwerden neu lesen -- Tab-Wechsel, Rueckkehr von der
   * Marketing-Seite.
   *
   * Der Schreibpfad ist davon unabhaengig abgesichert (er liest ohnehin
   * frisch), hier geht es um die ANZEIGE: die erste Aufgabe dieser Karte ist
   * ZEIGEN, und ein Zustand, den es nicht mehr gibt, ist auch als blosse
   * Anzeige eine Falschaussage ueber eine Einwilligung.
   */
  useEffect(() => {
    const neuLesen = () => {
      if (document.visibilityState === 'visible') setCookieConsent(effectiveConsent());
    };
    document.addEventListener('visibilitychange', neuLesen);
    return () => document.removeEventListener('visibilitychange', neuLesen);
  }, []);

  const getInitials = (email: string) => {
    return email.charAt(0).toUpperCase();
  };

  const handleDeleteAccount = async () => {
    setIsDeleting(true);
    try {
      const { error } = await supabase.functions.invoke('delete-user-account', {
        body: { removeFromEmailList },
      });

      if (error) throw error;

      toast.success(t('general.deleteSuccess'));
      await signOut();
      navigate('/auth');
    } catch (error: any) {
      console.error('Error deleting account:', error);
      // Fix-Runde 1: `error?.context?.status === 409` allein trifft JEDEN 409,
      // nicht nur ACTIVE_SUBSCRIPTION_EXISTS -- vorher zeigte ein
      // OWNER_MUST_TRANSFER-Ablehnung denselben Abo-Text, sogar fuer einen
      // Owner OHNE Subscription (`deleteBlockedDescription()` interpoliert
      // dann einen leeren Plannamen: eine sachlich falsche Anweisung, "kuendige
      // dein -Abonnement"). readOwnerMustTransfer liest den Body der echten
      // FunctionsHttpError und unterscheidet die beiden 409-Gruende zuerst.
      const ownerBlock = await readOwnerMustTransfer(error);
      if (ownerBlock) {
        toast.error(t('general.deleteBlockedOwnerTitle'), {
          description: t('general.deleteBlockedOwnerDesc', {
            merchantCenters: ownerBlock.merchantCenters.join(', '),
          }),
        });
      } else if (error?.context?.status === 409) {
        toast.error(t('general.deleteBlockedTitle'), { description: deleteBlockedDescription() });
      } else {
        Sentry.captureException(error, { tags: { feature: 'settings', action: 'delete-account' } });
        toast.error(t('general.deleteFailed'));
      }
    } finally {
      setIsDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div>
              <CardTitle>{t('general.accountInfo')}</CardTitle>
              <CardDescription>{t('general.accountDetails')}</CardDescription>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setShowDeleteDialog(true)}
              className="text-muted-foreground hover:text-destructive"
              aria-label={t('general.deleteAccount')}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center space-x-4">
            <Avatar className="h-16 w-16">
              <AvatarFallback className="text-xl bg-primary text-primary-foreground">
                {user?.email ? getInitials(user.email) : '?'}
              </AvatarFallback>
            </Avatar>
            <div>
              <p className="text-sm font-medium text-foreground">{t('general.emailAddress')}</p>
              <p className="text-sm text-muted-foreground">{user?.email}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Globe className="h-5 w-5 text-muted-foreground" />
            <div>
              <CardTitle>{t('general.language')}</CardTitle>
              <CardDescription>{t('general.languageDescription')}</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Select value={language} onValueChange={(v) => handleLanguageChange(v as 'de' | 'en')}>
            <SelectTrigger className="w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="de">Deutsch</SelectItem>
              <SelectItem value="en">English</SelectItem>
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Cookie-Entscheidung anzeigen und aendern. Steht neben der Sprachwahl,
          weil beides dieselbe Art von Entscheidung ist: eine Einstellung dieses
          Browsers, nicht des Kontos -- das Cookie haengt am Geraet, nicht am
          Konto, und gilt dort fuer Marketing-Seite und App gemeinsam. */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Cookie className="h-5 w-5 text-muted-foreground" />
            <div>
              <CardTitle>{t('general.cookieSettings')}</CardTitle>
              <CardDescription>{t('general.cookieSettingsDesc')}</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/*
            Der Zustand zuerst, und die beiden Faelle sind NICHT derselbe: "noch
            nicht entschieden" und "abgelehnt" sehen an den Schaltern gleich aus
            (beide aus) und sind es nicht. Die Unterscheidung traegt dieser Satz,
            nicht ein dritter Schalterzustand -- den gaebe das Cookie nicht her.

            Einem Unentschiedenen bietet die Karte deshalb auch KEINEN
            Ablehnen-Knopf an, und der Grund ist nicht Bequemlichkeit: sie ist
            keine Einwilligungs-Abfrage. Die Zwecke stehen hier nur in
            Halbsaetzen, es gibt kein gleichrangiges Ja/Nein und keine Angabe
            zum Verantwortlichen. Duerfte sie fuer Unentschiedene schreiben,
            verhinderte sie die vollstaendige Abfrage auf Basis einer
            Darstellung, die dafuer nicht reicht.

            Der Satz haengt am Regime, weil die Schalter allein dort luegen
            wuerden: im US-Default stehen sie auf an, ohne dass jemand etwas
            entschieden hat -- "gespeichert" waere falsch, "nicht entschieden,
            nichts aktiv" auch. Mit GPC stehen sie auf aus und sind gesperrt,
            und der Satz sagt warum. GPC geht VOR "gespeichert", denn das
            Signal schlaegt eine gespeicherte Entscheidung (E5). Ein Widerspruch
            im US-Regime schreibt dasselbe Cookie wie eine EU-Ablehnung (E8),
            danach heisst es hier wie dort "gespeichert".
          */}
          <p className="text-xs text-muted-foreground" data-testid="cookie-consent-state">
            {cookieGpcLocked
              ? t('general.cookieUsGpc')
              : parseConsentCookie() !== null
                ? t('general.cookieDecided')
                : consentRegime === 'us_optout'
                  ? t('general.cookieUsDefault')
                  : t('general.cookieUndecided')}
          </p>

          <div className="space-y-4" data-testid="cookie-consent-categories">
            {/*
              Notwendig ist nicht abwaehlbar und bekommt deshalb KEINEN
              Schalter: ein deaktivierter Schalter laedt zum Klicken ein und
              beantwortet die Frage "warum geht das nicht" nicht. Der Text
              daneben beantwortet sie.
            */}
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-medium">{t('general.cookieNecessary')}</p>
                <p className="text-xs text-muted-foreground">{t('general.cookieNecessaryDesc')}</p>
              </div>
              <span className="flex-shrink-0 text-xs text-muted-foreground">
                {t('general.cookieNecessaryAlways')}
              </span>
            </div>

            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-medium">{t('general.cookieAnalytics')}</p>
                <p className="text-xs text-muted-foreground">{t('general.cookieAnalyticsDesc')}</p>
              </div>
              <Switch
                checked={cookieAnalytics}
                disabled={cookieGpcLocked}
                onCheckedChange={(v) =>
                  saveCookieConsent({ analytics: v })
                }
                aria-label={t('general.cookieAnalytics')}
              />
            </div>

            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-medium">{t('general.cookieMarketing')}</p>
                <p className="text-xs text-muted-foreground">{t('general.cookieMarketingDesc')}</p>
              </div>
              <Switch
                checked={cookieMarketing}
                disabled={cookieGpcLocked}
                onCheckedChange={(v) =>
                  saveCookieConsent({ marketing: v })
                }
                aria-label={t('general.cookieMarketing')}
              />
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            {/*
              Ein Klick nimmt ALLES zurueck. Der Grund ist nicht Bequemlichkeit,
              sondern die Symmetrie, auf die es ankommt: erteilt wurde mit EINEM
              Klick auf "Alle akzeptieren" im Web-Banner. Muesste man hier zwei
              Schalter umlegen, waere der Widerruf muehsamer als die Erteilung.
              Ein Gegenstueck "Alles akzeptieren" gibt es hier bewusst nicht --
              Widerrufen darf leichter sein als Zustimmen, umgekehrt nicht.

              Er erscheint nur, wenn es etwas zurueckzunehmen gibt; sonst waere
              er ein Knopf ohne Wirkung, und der Satz darueber sagt bereits,
              dass nichts aktiv ist. Im US-Default ist etwas aktiv, also steht er
              dort; mit GPC gibt es nichts zu schreiben, also fehlt er.
            */}
            {(cookieAnalytics || cookieMarketing) && !cookieGpcLocked && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => saveCookieConsent({ analytics: false, marketing: false })}
              >
                {t('general.cookieRevokeAll')}
              </Button>
            )}
            {/*
              Der einzige Weg zur Datenschutzerklaerung, den diese App hat:
              keinen Footer, keine eigene Datenschutz-Route. Hier sucht ein
              Nutzer ihn, denn hier steht seine Cookie-Entscheidung.
            */}
            <a
              href={privacyPolicyUrl(language === 'de' ? 'de' : 'en')}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-muted-foreground underline hover:text-foreground"
            >
              {t('general.privacyLink')}
              <span className="sr-only"> ({t('general.newTab')})</span>
            </a>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-muted-foreground" />
            <div>
              <CardTitle>{t('general.emailNotifications')}</CardTitle>
              <CardDescription>{t('general.emailNotificationsDesc')}</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="email-lang">{t('general.emailLanguage')}</Label>
            <Select value={emailLang} onValueChange={(v) => updateEmailPreference('language', v)}>
              <SelectTrigger id="email-lang" className="w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="de">Deutsch</SelectItem>
                <SelectItem value="en">English</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">{t('general.emailLanguageDesc')}</p>
          </div>

          {/* `data-testid`: seit die Cookie-Karte eigene Schalter hat, ist
              "alle Schalter der Seite" nicht mehr dasselbe wie "die sechs
              E-Mail-Kategorien". Die Tests fragen seitdem hier nach. */}
          <div className="space-y-4" data-testid="email-category-switches">
            <Label>{t('general.emailCategories')}</Label>
            <p className="text-xs text-muted-foreground">{t('general.emailCategoriesNote')}</p>
            {unsubscribedAll && (
              <div
                data-testid="unsubscribed-all-notice"
                className="bg-warning-light border border-warning/30 rounded-lg px-4 py-3 flex items-center justify-between gap-3"
              >
                <p className="text-sm text-warning-ink">{t('general.unsubscribedAllNotice')}</p>
                <Button
                  size="sm"
                  onClick={() => updateEmailPreference('unsubscribed_all', false)}
                  disabled={isSavingPrefs}
                  className="flex-shrink-0"
                >
                  {t('general.reactivateEmails')}
                </Button>
              </div>
            )}
            {([
              { key: 'onboarding', label: t('general.onboarding'), desc: t('general.onboardingDesc') },
              { key: 'achievements', label: t('general.achievements'), desc: t('general.achievementsDesc') },
              { key: 'digests', label: t('general.reports'), desc: t('general.reportsDesc') },
              { key: 'upsell', label: t('general.upgradeSuggestions'), desc: t('general.upgradeSuggestionsDesc') },
              { key: 'reengagement', label: t('general.reengagement'), desc: t('general.reengagementDesc') },
              { key: 'review', label: t('general.reviewRequests'), desc: t('general.reviewRequestsDesc') },
            ] as const).map(({ key, label, desc }) => (
              <div key={key} className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">{label}</p>
                  <p className="text-xs text-muted-foreground">{desc}</p>
                </div>
                <Switch
                  checked={emailPrefs[key]}
                  onCheckedChange={(v) => updateEmailPreference(key, v)}
                  disabled={isSavingPrefs || unsubscribedAll}
                />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {hasActiveSubscription ? t('general.deleteBlockedTitle') : t('general.deleteConfirmTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              {hasActiveSubscription ? (
                <p>{deleteBlockedDescription()}</p>
              ) : (
                <>
                  <div className="rounded-md border border-destructive/50 bg-destructive/10 px-4 py-3 space-y-1">
                    <p className="font-semibold text-destructive-ink">{t('general.deleteWarning')}</p>
                    <p className="text-sm text-destructive-ink">{t('general.deleteIrreversibleNote')}</p>
                  </div>
                  <p>{t('general.deleteDescription')}</p>
                  <ul className="list-disc list-inside space-y-1 text-sm">
                    <li>{t('general.deleteItems.profile')}</li>
                    <li>{t('general.deleteItems.googleAccounts')}</li>
                    <li>{t('general.deleteItems.merchantCenters')}</li>
                    <li>{t('general.deleteItems.feeds')}</li>
                  </ul>
                  <div className="flex items-center gap-2 mt-4">
                    <Checkbox
                      id="remove-email-list"
                      checked={removeFromEmailList}
                      onCheckedChange={(v) => setRemoveFromEmailList(!!v)}
                    />
                    <Label htmlFor="remove-email-list" className="text-sm font-normal cursor-pointer">
                      {t('general.deleteRemoveFromEmailList')}
                    </Label>
                  </div>
                  <p className="font-medium mt-2">{t('general.deleteNoAccess')}</p>
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>
              {hasCancelledSubscription
                ? t('actions.gotIt', { ns: 'common' })
                : t('actions.cancel', { ns: 'common' })}
            </AlertDialogCancel>
            {hasActiveSubscription ? (
              // M8: nothing to manage once the plan is already cancelled — the
              // acknowledge button above is the whole interaction.
              hasCancelledSubscription ? null : (
                <AlertDialogAction
                  onClick={() => { setShowDeleteDialog(false); navigate('/settings?tab=subscription'); }}
                >
                  {t('general.goToSubscription')}
                </AlertDialogAction>
              )
            ) : (
              <AlertDialogAction
                onClick={handleDeleteAccount}
                disabled={isDeleting}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {isDeleting ? t('actions.deleting', { ns: 'common' }) : t('general.deleteAccount')}
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default GeneralTab;
